import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

// TEMPORARY: Import raw HTML files for About content preview
import howItWorksHtmlRaw from '../assets/about/how-it-works.html?raw';
import entryFeesHtmlRaw from '../assets/about/entry-fees.html?raw';
import prizePoolHtmlRaw from '../assets/about/prize-pool.html?raw';
import keyLeagueDatesHtmlRaw from '../assets/about/key-league-dates.html?raw';

import draftsHtmlRaw from '../assets/about/rules/drafts.html?raw';
import rulesOfGameHtmlRaw from '../assets/about/rules/rules-of-the-game.html?raw';
import freeAgencyHtmlRaw from '../assets/about/rules/free-agency.html?raw';
import tradesHtmlRaw from '../assets/about/rules/trades.html?raw';
import complianceHtmlRaw from '../assets/about/rules/compliance.html?raw';
import rosterMgmtHtmlRaw from '../assets/about/rules/roster-management.html?raw';

export interface EntryFeeInfo {
  league_fee?: number;
  team_fee_high?: number;
  team_fee_low?: number;
  currency?: string;
  payment_methods?: string[];
  refund_policy?: string;
  description?: string;
}

export interface PrizePoolInfo {
  total_prize_pool?: number;
  first_place?: number;
  second_place?: number;
  third_place?: number;
  distribution_details?: string;
  currency?: string;
  description?: string;
}

export interface KeyLeagueDate {
  name: string;
  date: string;
  description?: string;
  is_past?: boolean;
}

export interface AboutSettings {
  entry_fees: EntryFeeInfo;
  prize_pool: PrizePoolInfo;
  key_dates: KeyLeagueDate[];
  key_league_dates_html?: string; // TEMPORARY: HTML content for Key League Dates
  how_it_works?: {
    title?: string;
    content?: string;
    sections?: Array<{
      title: string;
      content: string;
    }>;
  };
  rules?: {
    title?: string;
    content?: string;
    sections?: Array<{
      title: string;
      content: string;
    }> | {
      rules_of_the_game?: string;
      roster_management?: string;
      compliance?: string;
      free_agency?: string;
      trades?: string;
      drafts?: string;
    };
  };
  league_id?: number;
}

export interface UseAboutSettingsReturn {
  aboutSettings: AboutSettings | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching: boolean;
}

/**
 * Extracts the inner HTML content from a full HTML document.
 * Prefers <main> content, falls back to <body> content.
 */
function extractMainOrBody(htmlString: string): string {
  if (!htmlString) return '';
  
  const trimmed = htmlString.trim();
  const looksLikeDocument =
    trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html');
  
  if (!looksLikeDocument) {
    return htmlString;
  }
  
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return htmlString;
  }
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const main = doc.querySelector('main');
  if (main) return main.innerHTML;
  const body = doc.querySelector('body');
  if (body) return body.innerHTML;
  return htmlString;
}

// TEMPORARY: Extract HTML content from raw imports
const howItWorksHtml = extractMainOrBody(howItWorksHtmlRaw);
const entryFeesHtml = extractMainOrBody(entryFeesHtmlRaw);
const prizePoolHtml = extractMainOrBody(prizePoolHtmlRaw);
const keyLeagueDatesHtml = extractMainOrBody(keyLeagueDatesHtmlRaw);

const draftsHtml = extractMainOrBody(draftsHtmlRaw);
const rulesOfGameHtml = extractMainOrBody(rulesOfGameHtmlRaw);
const freeAgencyHtml = extractMainOrBody(freeAgencyHtmlRaw);
const tradesHtml = extractMainOrBody(tradesHtmlRaw);
const complianceHtml = extractMainOrBody(complianceHtmlRaw);
const rosterMgmtHtml = extractMainOrBody(rosterMgmtHtmlRaw);

// Combine all rules sections into one HTML string in the correct order
const combinedRulesHtml = [
  rulesOfGameHtml,      // 1. rules-of-the-game.html
  rosterMgmtHtml,       // 2. roster-management.html
  complianceHtml,       // 3. compliance.html
  freeAgencyHtml,       // 4. free-agency.html
  tradesHtml,           // 5. trades.html
  draftsHtml,           // 6. drafts.html
]
  .filter(Boolean)
  .join('\n\n');

/**
 * TODO: Replace this placeholder function with a real API call once the backend endpoint is implemented.
 * Expected endpoint: GET /about/settings or GET /leagues/${leagueId}/about-settings
 * 
 * This function should be replaced with:
 * ```typescript
 * const fetchAboutSettings = async (leagueId?: number): Promise<AboutSettings> => {
 *   const endpoint = leagueId 
 *     ? `/leagues/${leagueId}/about-settings`
 *     : '/about/settings';
 *   const { data } = await apiClient.get(endpoint);
 *   return data;
 * };
 * ```
 * 
 * TEMPORARY: Using raw HTML assets for About content so we can test the
 * `.about_content` wrapper, CSS, and Drafts behavior before the DB is wired.
 * Replace these with API-provided HTML when about-settings is implemented.
 */
const fetchAboutSettings = async (leagueId?: number): Promise<AboutSettings> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 100));

  // TEMPORARY: Return HTML content from raw imports
  return {
    entry_fees: {
      description: entryFeesHtml,
      currency: 'USD',
    },
    prize_pool: {
      description: prizePoolHtml,
      currency: 'USD',
    },
    key_dates: [
      {
        name: 'Registration Deadline',
        date: '2024-03-01',
        description: 'Last day to register for the league',
        is_past: false,
      },
      {
        name: 'Season Start',
        date: '2024-04-01',
        description: 'Official start of the season',
        is_past: false,
      },
    ],
    key_league_dates_html: keyLeagueDatesHtml, // TEMPORARY: HTML content for Key League Dates
    how_it_works: {
      title: 'How It Works',
      content: howItWorksHtml,
    },
    rules: {
      title: 'Rules',
      content: combinedRulesHtml, // keep for compatibility
      sections: {
        rules_of_the_game: rulesOfGameHtml,
        roster_management: rosterMgmtHtml,
        compliance: complianceHtml,
        free_agency: freeAgencyHtml,
        trades: tradesHtml,
        drafts: draftsHtml,
      },
    },
    league_id: leagueId,
  };
};

export function useAboutSettings(leagueId?: number): UseAboutSettingsReturn {
  const { data, isLoading, error, refetch, isFetching } = useQuery<AboutSettings>({
    queryKey: ['aboutSettings', leagueId],
    queryFn: () => fetchAboutSettings(leagueId),
    enabled: !!leagueId, // Only fetch if leagueId is provided (league-specific)
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
  });

  return {
    aboutSettings: data ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
    isFetching,
  };
}

// TODO: Replace placeholder about settings with a real API call once the DB and /about settings endpoint are implemented.

